import { Link as RouterLink, useLocation } from 'react-router-dom'
import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import { QueryStatus, useQuery } from '@owl-nest/hooks'
import { Either } from '@owl-nest/monad'
import * as ds from '@ulule/owl-kit-components/next'
import { t } from '@owl-nest/localize'
import * as api from '@owl-nest/api-client/latest'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'

import { Project } from '../../../../models/project'
import { PATHS } from '../../../../paths'
import { RootState } from '../../../../../reducers'
import { State as CommentState } from '../../../../../reducers/comments'
import * as commentAction from '../../../../../actions/comments'
import * as Styles from '../../style'
import { Comments } from '../../components/Comments'
import { News } from './News'
import { useNavigation } from '../../../../hooks/useNavigation'
import { useDisplayMode } from '../../../../hooks/useDisplayMode'

type NewsPermalinkProps = {
  news: api.News<true, true>
  id: number
}

export function NewsPermalink({ news, id }: NewsPermalinkProps): React.ReactElement<NewsPermalinkProps> {
  const project = Redux.useSelector<RootState, Project>((state) => state.application.project.data)
  const auth = useAuth()
  const navigation = useNavigation()
  const location = useLocation()
  const displayMode = useDisplayMode(project)
  const isPreview = displayMode === 'preview'

  const children = Redux.useSelector<RootState, CommentState['list']['root']>(
    (state) => state.application.comments.list.root,
  )
  const dispatch = Redux.useDispatch()

  const isLoggedIn = auth.type === 'loggedin'
  const [query, response] = useQuery(async () => {
    if (news?.id !== id || news === undefined) {
      const result = await api.api.get.news({
        urlParams: { newsId: String(id) },
        withToken: isLoggedIn,
      })
      return result.next((success) => success.body)
    }

    return Either.right(news)
  })

  React.useEffect(() => {
    if (response.status === QueryStatus.SUCCESS) {
      dispatch(
        commentAction.loadRoot(
          { type: 'news', news: news || response.data },
          { pagination: `?limit=${20}&since=${Math.floor(Date.now() / 1000)}`, forceClean: true },
        ),
      )
    }
  }, [response.status])

  React.useEffect(() => {
    if (news?.id !== id) {
      query()
    }
  }, [id])

  if (response.status === QueryStatus.PRISTINE) {
    query()
  }

  if (response.status !== QueryStatus.SUCCESS) {
    return (
      <Styles.Main>
        <Styles.Wrapper>
          <Styles.Content>
            <ds.Spinner />
          </Styles.Content>
        </Styles.Wrapper>
      </Styles.Main>
    )
  }

  news = response.data || news

  const isUserStaff = auth.type === 'loggedin' && auth.user.is_staff
  const isUserAllowed = Boolean(news.permissions.self?.includes('read'))
  const projectTeamRoles = [api.UserRole.Owner, api.UserRole.Editor, api.UserRole.Moderator]
  const isProjectTeam = project.user_role && projectTeamRoles.includes(project.user_role)
  const isNewsMasked = !isUserAllowed
  const isWaiting = news.status === api.NewsStatus.WAITING

  let hideComments = false
  if (isNewsMasked || isWaiting) {
    // Comments are hidden for masked news and draft ones
    hideComments = true
  } else if (project.comments_enabled === api.ProjectCommentsPermission.NOONE && !isProjectTeam && !isUserStaff) {
    // Also, only staff and project team can access comments when they are disabled
    hideComments = true
  }

  return (
    <Styles.Main>
      <Anchor id="anchor" isPreview={isPreview} />
      <Styles.Wrapper>
        <Styles.Content>
          <BackToNewsStyled forwardedAs={RouterLink} to={navigation.pathTo(PATHS.NEWS)}>
            <ds.glyphs.stroke.ArrowLeft size={15} />
            {t('See all posts')}
          </BackToNewsStyled>

          <News news={news} full />

          {!hideComments && (
            <Comments
              autoFocus={location.hash === '#root-comment-form'}
              rootList={children}
              onLoadMore={(next) => dispatch(commentAction.loadRoot({ type: 'news', news }, { pagination: next }))}
              onRootSubmit={async (values) => {
                await dispatch(commentAction.createRoot({ type: 'news', news }, values.comment))
              }}
            />
          )}
        </Styles.Content>
      </Styles.Wrapper>
    </Styles.Main>
  )
}

const BackToNewsStyled = styled(ds.Link).attrs((props) => ({ ...props, kind: 'secondary' }))`
  display: block;
  margin-bottom: 31px;

  ${ds.glyphs.stroke.ArrowLeft} {
    margin-right: 8px;
  }
`

const Anchor = styled.div<{ isPreview: boolean }>`
  position: absolute;
  top: ${({ isPreview }) => (isPreview ? '-108px' : '-60px')};
`
